import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { toast } from 'sonner'
import * as feedbacksAPI from '@/api/feedbacks'
import { feedbackKeys } from './feedbackKeys'
import type { FeedbackStats, Feedback, CreateFeedbackRequest } from '@/types/performance'

// ================================
// QUERIES
// ================================

/**
 * Use Feedback Stats Query
 *
 * Fetches aggregated feedback statistics for current user.
 * Cached for 1 minute - background refresh on stale.
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading, isFetching } = useFeedbackStats()
 * // stats = { sent: 10, received: 5, byCategory: {...} }
 * ```
 */
export function useFeedbackStats() {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: feedbackKeys.stats(tenantDomain!),
    queryFn: async (): Promise<FeedbackStats> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await feedbacksAPI.getFeedbackStats()
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Use Recent Feedbacks Query
 *
 * Fetches most recent feedbacks for current user.
 * Cached for 30 seconds - fresher data for recent list.
 *
 * @example
 * ```tsx
 * const { data: feedbacks, isLoading } = useRecentFeedbacks()
 * // feedbacks = [{ feedbackId, category, toEmployeeName, ... }]
 * ```
 */
export function useRecentFeedbacks() {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: feedbackKeys.recent(tenantDomain!),
    queryFn: async (): Promise<Feedback[]> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await feedbacksAPI.getRecentFeedbacks()
    },
    enabled: !!tenantDomain,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use Feedbacks List Query
 *
 * Paginated list of all feedbacks for current user.
 * Supports filtering and pagination.
 *
 * @param params - Optional pagination parameters
 * @param params.type - Filter by 'received' (default) or 'sent' feedbacks
 * @example
 * ```tsx
 * const { data, isLoading } = useFeedbacksList({ limit: 20, type: 'sent' })
 * // data = { items: [...], pagination: { nextToken, itemCount } }
 * ```
 */
export function useFeedbacksList(params?: { limit?: number; nextToken?: string; type?: 'received' | 'sent' }) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: feedbackKeys.list(tenantDomain!, params),
    queryFn: async () => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await feedbacksAPI.listFeedbacksMe(params)
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000,
  })
}

// ================================
// MUTATIONS
// ================================

/**
 * Use Create Quick Feedback Mutation
 *
 * Creates quick feedback with optimistic updates.
 * Invalidates stats and recent feedbacks on success.
 *
 * @example
 * ```tsx
 * const createFeedback = useCreateQuickFeedback()
 *
 * await createFeedback.mutateAsync({
 *   toEmployeeId: 'employee-123',
 *   category: 'Reconhecimento',
 *   context: '...',
 *   observation: '...',
 *   impact: '...',
 *   nextSteps: '...'
 * })
 * ```
 */
export function useCreateQuickFeedback() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateFeedbackRequest) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await feedbacksAPI.createQuickFeedback(data)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('Feedback enviado com sucesso!')

      // Invalidate stats to update counters
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.stats(tenantDomain),
      })

      // Invalidate recent feedbacks to show new feedback
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.recent(tenantDomain),
      })
    },

    onError: (err) => {
      console.error('[CreateQuickFeedback] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao enviar feedback'
      )
    },
  })
}

/**
 * Use Create Complete Feedback Mutation
 *
 * Creates complete feedback with full COIN/SBI validation.
 * Invalidates stats and recent feedbacks on success.
 */
export function useCreateCompleteFeedback() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateFeedbackRequest) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await feedbacksAPI.createCompleteFeedback(data)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('Feedback completo enviado com sucesso!')

      queryClient.invalidateQueries({
        queryKey: feedbackKeys.stats(tenantDomain),
      })
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.recent(tenantDomain),
      })
    },

    onError: (err) => {
      console.error('[CreateCompleteFeedback] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao enviar feedback'
      )
    },
  })
}

// ================================
// UTILITY HOOKS
// ================================

/**
 * Invalidate Feedback Cache
 *
 * Manually invalidate feedback cache to force refetch.
 * Useful for manual refresh buttons.
 *
 * @example
 * ```tsx
 * const invalidateFeedbacks = useInvalidateFeedbackCache()
 *
 * <Button onClick={() => invalidateFeedbacks()}>
 *   Refresh
 * </Button>
 * ```
 */
export function useInvalidateFeedbackCache() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return () => {
    if (!tenantDomain) return

    queryClient.invalidateQueries({
      queryKey: feedbackKeys.all,
    })
  }
}
